import React, { FC, cloneElement, ReactElement } from 'react';
import {
  DateField,
  List,
  TextField,
  TopToolbar,
  ToolbarProps,
  ExportButtonProps,
  ListProps,
  sanitizeListRestProps,
  useListContext,
} from 'react-admin';
import { makeStyles } from '@material-ui/core';

import Datagrid from 'components/Datagrid';
import CustomExportButton from 'components/CustomExportButton';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_SEE_RECEIVABLE_INVOICE_COLUMNS } from 'providers/permissions';
import { exporter } from 'providers/export';

import { ReceivableRegistryProvider } from '../hooks/ReceivableRegistry';
import { InvoiceDraftRegistryProvider } from '../hooks/InvoiceDraftRegistry';
import { JobPhaseReceivableSheetFilter } from '../filters/job-phase-receivable-sheet-filters';
import { AddressField } from '../components/AddressField';
import { ReceivableCheckboxField } from '../components/ReceivableCheckboxField';
import { InvoicePOAmountField } from '../components/InvoicePOAmountField';
import { InvoiceNumberField } from '../components/InvoiceNumberField';
import { InvoiceHistoryField } from '../components/InvoiceHistoryField';
import { InvoiceSaveField } from '../components/InvoiceSaveField';

const useStyles = makeStyles({
  content: {
    overflow: 'auto',
  },
  root: {
    width: 'calc(100vw - 150px)',
  },
});

type ListActionProps = ToolbarProps &
  Pick<ExportButtonProps, 'maxResults'> & {
    filters?: ReactElement;
  };

const ListActions: FC<ListActionProps> = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    total,
  } = useListContext();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

export const JobPhaseReceivableSheetList: FC<ListProps> = props => {
  const classes = useStyles();
  const { hasPermission } = PermissionsProvider.useContainer();

  const canSeeInvoiceColumns = hasPermission(
    CAN_SEE_RECEIVABLE_INVOICE_COLUMNS,
  );

  return (
    <ReceivableRegistryProvider>
      <InvoiceDraftRegistryProvider>
        <List
          {...props}
          classes={{
            content: classes.content,
            root: classes.root,
          }}
          exporter={exporter('job-phase-receivable-sheet', {
            canSeeInvoiceColumns,
          })}
          filters={<JobPhaseReceivableSheetFilter>{props.filters}</JobPhaseReceivableSheetFilter>}
          sort={{ field: 'dateApproved', order: 'DESC' }}
          bulkActionButtons={false}
          actions={<ListActions maxResults={20000} />}
        >
          <Datagrid>
            <TextField source="approvedByName" label="Approved By" />
            <DateField source="dateApproved" label="Approval Date" />
            <TextField source="phaseName" label="Phase" />
            <TextField source="regionName" label="Region" />
            <TextField source="builderName" label="Builder" />
            <TextField source="subdivisionName" label="Subdivision" />
            <TextField source="lot" label="Lot" />
            <TextField source="block" label="Block" />
            <AddressField label="Address" />
            {canSeeInvoiceColumns && (
              <ReceivableCheckboxField
                field="supplyProIntegrated"
                label="SupplyPro ClickOff"
              />
            )}
            {canSeeInvoiceColumns && (
              <ReceivableCheckboxField field="billed" label="Billed" />
            )}
            {canSeeInvoiceColumns && (
              <InvoicePOAmountField label="Invoice PO Amount" />
            )}
            {canSeeInvoiceColumns && (
              <InvoiceNumberField label="Invoice #" />
            )}
            {canSeeInvoiceColumns && <InvoiceHistoryField label="" />}
            {canSeeInvoiceColumns && <InvoiceSaveField label="" />}
          </Datagrid>
        </List>
      </InvoiceDraftRegistryProvider>
    </ReceivableRegistryProvider>
  );
};

export default JobPhaseReceivableSheetList;
